<script setup lang="ts">
import { mdiLoading } from "@mdi/js";
import PrimeVueButton, { ButtonProps } from "primevue/button";
import { tw } from "~/utils/constants";

interface IButton extends /* @vue-ignore */ Pick<ButtonProps, "pt" | "raised" | "label"> {
	id?: string;
	variant?: "primary" | "secondary" | "tertiary" | "identity" | "identity-inverse" | "custom";
	size?: "small" | "medium" | "large" | "custom";
	icon?: string;
	disabled?: boolean;
	iconViewBox?: string | undefined;
	iconClass?: string | string[] | undefined;
	loadingIconClass?: string | string[] | undefined;
	label?: string;
	labelClass?: string | string[] | undefined;
	ariaLabel?: string;
	loading?: boolean;
	iconPos?: "left" | "right";
	rootClass?: string | Record<string, boolean> | (string | Record<string, boolean>)[] | TemplateStringsArray;
	truncate?: boolean;
	position?: "static" | "relative" | "absolute" | "fixed" | "sticky";
}

const props = withDefaults(defineProps<IButton>(), {
	id: "",
	size: "large",
	variant: "primary",
	icon: "",
	label: "",
	iconPos: "left",
	iconViewBox: undefined,
	iconClass: undefined,
	loadingIconClass: undefined,
	loading: false,
	rootClass: "",
	truncate: false,
	position: "relative",
});
const baseClasses = {
	root: tw`rounded transition-all disabled:bg-light-400 disabled:text-dark-100 dark:disabled:text-dark-100 font-bold text-sm outline-none whitespace-nowrap dark:disabled:bg-dark-700`,
};

const sizeClass = {
	root: {
		small: tw`px-2 h-7`,
		medium: tw`px-4 h-9`,
		large: tw`px-4 h-10`,
		custom: "",
	},
};

const variantClass = {
	root: {
		primary: tw`bg-primary-600 text-light-50 hover:bg-primary-500 focus:bg-primary-400`,
		secondary: tw`bg-dark-800 dark:bg-dark-600 dark:hover:bg-dark-800 text-light-50 dark:focus:bg-dark-400 hover:bg-dark-600 focus:bg-dark-900`,
		tertiary: tw`bg-light-50 dark:bg-dark-800 dark:hover:bg-dark-700 dark:focus:bg-dark-700 dark:text-light-50 text-dark-600 hover:bg-light-200 focus:bg-light-200`,
		identity: tw`bg-identity hover:bg-identity-hover text-light-50`,
		"identity-inverse": tw`bg-light-50 text-identity hover:bg-light-200`,
		custom: tw``,
	},
};
</script>

<template>
	<PrimeVueButton
		:disabled="disabled ? true : false"
		:aria-label="ariaLabel || label"
		:id="id"
		:pt="{
			root: {
				class: [baseClasses.root, sizeClass.root[props.size], variantClass.root[props.variant], props.rootClass, props.position],
			},
		}"
	>
		<div v-if="loading" class="absolute top-0 left-0 flex items-center justify-center w-full h-full">
			<UiIcon :path="mdiLoading" class="animate-spin" :class="loadingIconClass || 'fill-[currentColor] w-5 h-5'" />
		</div>
		<div :class="[{ 'text-[transparent]': loading }, 'flex justify-center items-center text-center w-full ']">
			<slot name="icon-left">
				<UiIcon
					v-if="icon && iconPos === 'left'"
					name="icon"
					:path="icon"
					:class="[disabled && 'grayscale', iconClass || 'mr-1 fill-[currentColor] w-5 h-5']"
					:viewBox="iconViewBox"
				/>
			</slot>

			<span :class="[labelClass, { 'overflow-hidden text-ellipsis': truncate }]">
				{{ label }}
			</span>

			<UiIcon
				v-if="icon && iconPos === 'right'"
				name="icon"
				:path="icon"
				:class="[disabled && 'grayscale', iconClass || 'ml-1 fill-[currentColor] w-5 h-5']"
				:viewBox="iconViewBox"
			/>
		</div>
	</PrimeVueButton>
</template>
